package diana.soleil.design.iraniancanadienbazzar.feature.categories

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.CategoryRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.CityController
import diana.soleil.design.iraniancanadienbazzar.core.model.Category
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

/** Immutable UI state for the Categories screen (UDF). */
data class CategoriesUiState(
    val loading: Boolean = true,
    val categories: List<Category> = emptyList(),
    val error: Boolean = false,
)

/**
 * Drives [CategoriesScreen] off [CategoryRepository]. Stateless screen + this stateful holder is the
 * pattern every screen follows (see `docs/ARCHITECTURE.md`).
 *
 * `businessCount` comes straight from the category docs: `recomputeCategoryCount` (Functions)
 * maintains it — and the per-metro / per-country maps — on every business write; this holder
 * swaps in the figure for the active scope (see [Category.countFor]). This screen briefly re-derived
 * counts from a full-directory stream to paper over seed-era drift — that cost the entire national
 * corpus per tab visit; the drift is fixed at the source by `scripts/backfill-category-counts.mjs`.
 */
class CategoriesViewModel(
    private val categoryRepository: CategoryRepository,
    private val cityController: CityController,
) : ViewModel() {

    private val _state = MutableStateFlow(CategoriesUiState())
    val state: StateFlow<CategoriesUiState> = _state.asStateFlow()

    init {
        observe()
    }

    /** Re-subscribe after an error. */
    fun retry() {
        _state.value = CategoriesUiState(loading = true)
        observe()
    }

    private fun observe() {
        viewModelScope.launch {
            // The tile count must match what tapping the tile lists (the active metro/country),
            // so the per-scope figure replaces the global one before the screen sees it.
            combine(
                categoryRepository.observeCategories(),
                cityController.activeMetroId,
                cityController.activeCountry,
            ) { result, metroId, country ->
                when (result) {
                    is DataResult.Success -> CategoriesUiState(
                        loading = false,
                        categories = result.data.map { category ->
                            category.copy(businessCount = category.countFor(metroId, country.code))
                        },
                    )
                    is DataResult.Failure -> CategoriesUiState(loading = false, error = true)
                }
            }.collect { _state.value = it }
        }
    }
}
